<h2 mat-dialog-title>How to Split a PDF</h2>
<mat-dialog-content>
  <p>
    Audit operates according to a golden rule: One File, One Statement. This means that each PDF file that you upload should contain a single
    statement. This pattern isn’t just useful for document organization — when the One File, One Statement rule is followed, the Staitment AI is
    better able to optimize your document and maximize your savings.
  </p>
  <p>
    Sometimes statements are consolidated in a way that breaches the One File, One Statement rule. This might happen if you combine the statements
    from multiple merchant locations, or from multiple months into a single PDF file. There are multiple easy ways to reorganize pages
    of a PDF into new files, each adhering the One File, One Statement rule. This process is called "splitting."
  </p>
  <p>
    In the following sections, we will introduce you to several online tools that you can use to split PDF files. Costs and benefits will be
    described in each. As you use your selected option, remember the golden rule: one file, one statement.
  </p>

  <hr>

  <h3>ILovePDF</h3>
  <ul>
    <li>Benefits: Easy to use, works in browser, free</li>
    <li>Costs: No online storage</li>
  </ul>
  <p>
    This is the best option for most users. Like other browser-based services, ILovePDF offers an online PDF splitting service that is easy to use and
    completely free. Simply upload your document, select the page ranges that accurately divide the document, and download a .zip file of the results.
  </p>
  <h4>How to use:</h4>
  <ul class="ml-1">
    <li>Go to the
      <a href="https://www.ilovepdf.com/split_pdf" target="_blank" class="mat-text-primary">ILovePDF website</a>
        and select Split PDF from the top toolbar.
    </li>
    <li>Click Select PDF File and choose a file to upload.</li>
    <li>Using page numbers, select the appropriate range numbers to divide your document into independent PDFs.</li>
    <ul>
      <li>Custom Range allows you to enter page ranges of variable sizes (e.g. pages 1-3, pages 4-9, pages 10-14).</li>
      <li>Fixed range will divide the entire document into subsections of a fixed length.</li>
      <li>Add a range of either option using the Add Range button.</li>
    </ul>
  </ul>

  <hr>

  <h3>PDF Converter</h3>
  <ul>
    <li>Benefits: Easy to use, works in browser, free exploit</li>
    <li>Costs: Monthly subscription needed for convenient use</li>
  </ul>
  <p>
    PDF Converter comes with most of the same features as Adobe Acrobat and Smallpdf, but with one key difference: there's a way to use it's split
    feature for free, without purchasing a subscription. The key here is in cookies, the invisible tags that websites use to track users. Users can
    disguise their cookies using a single step that is simple, but not convenient. Details are described below.
  </p>
  <h4>How to use:</h4>
  <ul class="ml-1">
    <li>Open a <i>private browser</i> by pressing the keys <code>Ctrl+Shift+N</code> (Windows) or <code>Cmd+Shift+N</code> (Mac). A private browsing
        window will not save your browsing history, and hide cookies from the sites you visit.
    </li>
    <li>Visit the
      <a href="https://www.freepdfconvert.com/split-pdf" target="_blank" class="mat-text-primary">PDF Converter website</a>
        and navigate to the split tool.
    </li>
    <li>Upload your combined PDF and click the lines between pages to mark the starting and ending points of the new child PDF documents.</li>
    <li>Download your new PDFs. They should appear in a zipped folder, which you can unzip to find the post-split PDFs.</li>
    <li>Close the private browser window (not just the tab). If you need to split another document, restart at step one.</li>
    <li>If you do this process in a normal browser and without a paid subscription, PDF Converter might give you a one-hour cooldown before you can
        split a new PDF. Hiding your cookies with a private browser prevents this cooldown timer from appearing.
    </li>
  </ul>

  <hr>

  <h3>Adobe Acrobat</h3>
  <ul>
    <li>Benefits: Easy to use, works in browser, most powerful</li>
    <li>Costs: Monthly subscription needed ($12-$16)</li>
  </ul>
  <p>
    Adobe created the PDF file format, and the tools offered in their powerful Acrobat suite are as good as they get. We love their splitting tool's
    simple user interface and automatic cloud storage. A monthly subscription is needed to use this tool after processing the free document. This is a
    great tool to have for those who frequently need to edit, sign, or manage PDFs in their work.
  </p>
  <h4>How to use:</h4>
  <ul class="ml-1">
    <li>Go to the
      <a href="https://documentcloud.adobe.com/" target="_blank" class="mat-text-primary">Adobe Document Cloud website</a>
        and create or log in to your account.
    </li>
    <li>Hover over the "Edit" tab in the toolbar at the top of the page. A dropdown menu appears.</li>
    <li>Find the subsection titled "Merge & organize" in the dropdown menu. Click "Split PDF" - it should be the second item from the bottom.</li>
    <li>Upload a document, then click the dotted lines between pages to delineate the start and stop points of the new PDF files created by the
        split.
    </li>
    <li>
      Complete the split and choose or create a cloud storage folder where the new documents will be stored. You can download the new PDF files from
      this cloud folder.
    </li>
  </ul>

  <hr>

  <h3>Smallpdf</h3>
  <ul>
    <li>Benefits: Easy to use, works in browser, simplest</li>
    <li>Costs: Monthly subscription needed ($9-$12)</li>
  </ul>
  <p>
    Smallpdf is a simple and lightweight cousin of Adobe Acrobat. It also offers cloud storage of documents and an easy way to split and merge
    PDFs. It also requires a subscription, but offers a few cheaper payment plans than Adobe.
  </p>
  <h4>How to use:</h4>
  <ul class="ml-1">
    <li>Visit the
      <a href="https://smallpdf.com" target="_blank" class="mat-text-primary">Smallpdf website</a>
        and create or log in to your account. You can also create a 7 day free trial but you have to
        enter payment information.
    </li>
    <li>Navigate to the
      <a href="https://smallpdf.com/split-pdf" target="_blank" class="mat-text-primary">split tool</a>
        and upload your combined PDF.
    </li>
    <li>Click the lines between pages to mark the starting and ending points of the new PDF files to be created by the split.</li>
    <li>Complete the split. Download your new PDFs that are in a zipped folder.</li>
  </ul>

  <hr>

  <h3>Apple Preview</h3>
  <ul>
    <li>Benefits: Out-of-the-box option, free</li>
    <li>Costs: No browser support, Mac only</li>
  </ul>
  <p>
    Apple Preview, which comes with all Mac computers, is a free way to split PDF options. Unlike the browser based options, which you can simply open
    in a new tab, this option requires you to do a little local file management. A Mac-exclusive option.
  </p>
  <h4>How to use:</h4>
  <ul class="ml-1">
    <li>In the Preview app, click View > Thumbnails to see a sidebar with thumbnails of the list of pages in the PDF.</li>
    <li>Drag and drop a page from the Thumbnails pane onto your desktop - you'll get a new PDF file that just contains that page.</li>
    <li>Open the new PDF file that contains the one page in a separate Preview window.</li>
    <li>Drag and drop the rest of the statement pages from the original file into the new PDF file's sidebar.</li>
    <li>When you're done, click File > Save on the new PDF file of a single statement.</li>
    <li>Repeat, starting at step two, for each statement within the original file.</li>
  </ul>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button mat-dialog-close>Close</button>
</mat-dialog-actions>